import React, { useState } from 'react';
import { KanbanBoard } from '../components/tasks/KanbanBoard';
import { TaskDetailSlideOver } from '@shared/components/tasks/TaskDetailSlideOver';
import { Button } from '@shared/components/ui/button';
import { Switch } from '@shared/components/ui/switch';
import { Label } from '@shared/components/ui/label';
import { useTasksQuery } from '@shared/hooks/useTasksQuery';
import { useNotesQuery } from '@shared/hooks/useNotesQuery';
import { CheckSquare } from 'lucide-react';
import BaseNav from '@shared/components/common/BaseNav';
import FloatingNav from '@/components/common/FloatingNav';

export default function TasksPage() {
  const [selectedTaskId, setSelectedTaskId] = useState<string | null>(null);
  const [showArchived, setShowArchived] = useState(false);

  const { data: tasks = [], isLoading } = useTasksQuery(showArchived);
  const { data: notes = [] } = useNotesQuery();

  const visibleTasks = showArchived ? tasks : tasks.filter(t => !t.is_archived);
  // Derive the open task from live query data so slide-over edits reflect instantly.
  const selectedTask = tasks.find(t => t.task_id === selectedTaskId) ?? null;

  return (
    <div className={`flex flex-col h-screen pt-14 transition-[margin] duration-200 ${selectedTask ? 'sm:mr-[540px]' : ''}`}>
      <BaseNav />
      {/* Header */}
      <div className="h-12 flex items-center justify-between px-6 border-b border-border shrink-0">
        <div className="flex items-center gap-2">
          <CheckSquare className="h-4 w-4 text-primary" />
          <span className="font-semibold text-sm">Tasks</span>
        </div>
        <div className="flex items-center gap-2">
          <Label htmlFor="show-archived" className="text-xs text-muted-foreground cursor-pointer">
            Show archived
          </Label>
          <Switch
            id="show-archived"
            checked={showArchived}
            onCheckedChange={setShowArchived}
            className="scale-75"
          />
        </div>
      </div>

      {/* Board */}
      <div className="flex-1 overflow-auto pt-4">
        {isLoading ? (
          <div className="flex items-center justify-center h-full text-muted-foreground text-sm">
            Loading tasks...
          </div>
        ) : (
          <KanbanBoard tasks={visibleTasks} onCardClick={(t) => setSelectedTaskId(t.task_id)} />
        )}
      </div>

      <TaskDetailSlideOver
        task={selectedTask}
        open={!!selectedTask}
        onClose={() => setSelectedTaskId(null)}
        allTasks={tasks}
        notes={notes}
      />

      <FloatingNav />
    </div>
  );
}
